import { useState, useEffect } from "react";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientSelect } from "@/components/ClientSelect";
import { BadgePercent } from "lucide-react";
import { api, money } from "@/lib/api";

export const TIER_LABELS = { basic: "Basic", standard: "Standard", site: "Site / Address", premium: "Premium", franchise: "Franchise Study", comparison: "Franchise Comparison" };

export const InvoiceDialog = ({ open, onOpenChange, clients, studies, settings, initial, onSubmit }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm({ client_id: "", study_id: "", tier: "standard", amount: "", description: "", due_date: "", ...(initial || {}) });
  }, [open, initial]);
  const [offer, setOffer] = useState(null);
  useEffect(() => { setOffer(null); if (open && form.client_id) api.get(`/clients/${form.client_id}/offer`).then((r) => setOffer(r.data)).catch(() => {}); }, [open, form.client_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tierPrice = settings?.tiers?.[form.tier];
  const clientStudies = studies.filter((s) => !form.client_id || s.client_id === form.client_id || !s.client_id);
  const field = "bg-slate-950/60 border-slate-700";

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, study_id: form.study_id || null, amount: form.amount ? Number(form.amount) : null, due_date: form.due_date || null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New invoice</DialogTitle>
          <DialogDescription>The client gets a secure link to pay online with PayPal.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Client *</Label>
            <ClientSelect value={form.client_id} onChange={(v) => set("client_id", v)} clients={clients} allowNone={false} testId="invoice-client" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Study</Label>
            <Select value={form.study_id || "none"} onValueChange={(v) => set("study_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="invoice-study" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="none">No linked study</SelectItem>
                {clientStudies.map((s) => <SelectItem key={s.id} value={s.id}>{s.input.project_name || s.input.address || "Untitled"}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Tier</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TIER_LABELS).map(([k, l]) => (
                <button type="button" key={k} data-testid={`invoice-tier-${k}`} onClick={() => setForm((f) => ({ ...f, tier: k, amount: "" }))}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.tier === k ? "border-emerald-500 bg-emerald-500/10" : "border-slate-700 hover:border-slate-500"}`}>
                  <p className="text-sm font-semibold">{l}</p><p className="num text-xs text-slate-400">{money(settings?.tiers?.[k], settings?.currency)}</p>
                </button>
              ))}
            </div></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Amount override</Label>
              <Input data-testid="invoice-amount" type="number" min="1" step="0.01" placeholder={String(tierPrice ?? "")} value={form.amount} onChange={(e) => set("amount", e.target.value)} className={`${field} num`} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Due date</Label>
              <DatePicker testId="invoice-due" value={form.due_date} onChange={(v) => set("due_date", v)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Description</Label>
            <Input data-testid="invoice-description" placeholder={`${TIER_LABELS[form.tier] || ""} feasibility study`} value={form.description} onChange={(e) => set("description", e.target.value)} className={field} /></div>
          {offer && <p data-testid="invoice-offer-notice" className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-200"><BadgePercent className="h-4 w-4 shrink-0" />A {offer.percent}% repeat-client discount will be applied automatically (offer valid until {offer.expires_at.slice(0, 10)}).</p>}
          <Button data-testid="invoice-create-btn" disabled={!form.client_id} className="w-full rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Create invoice</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
